import { Injectable, OnDestroy } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormControl, ValidatorFn, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { FormsBase } from '../forms-base/forms-base';
import {
   numericCharactersValidator,
   specialCharactersValidator,
   upperCaseCharactersValidator,
} from '../validators/password.validator';
import { PasswordStrengthSettingsModel } from './password-strength-settings.model';

@Injectable()
export class PasswordFormService extends FormsBase implements OnDestroy {
   confirmNewPasswordControl: UntypedFormControl;

   newPasswordControl: UntypedFormControl;

   passwordStrengthRules: PasswordStrengthSettingsModel | undefined;

   private _passwordEnteredOnFormControl: string | null;

   private destroyed$ = new Subject<void>();

   constructor(private formBuilder: UntypedFormBuilder) {
      super();
   }

   getPasswordRules(): PasswordStrengthSettingsModel | undefined {
      if (this.passwordStrengthRules) {
         return this.passwordStrengthRules;
      }
   }

   initResetPasswordViewForm(): void {
      this.form = this.formBuilder.group({
         ...this.createPasswordForm(),
      });
   }

   initUserChangePasswordViewForm(): void {
      this.form = this.formBuilder.group({
         ...this.createPasswordForm(),
         oldPassword: this.formBuilder.control(null),
      });
   }

   markControlsTouched(): void {
      this.newPasswordControl.markAllAsTouched();
      this.confirmNewPasswordControl.markAllAsTouched();
   }

   ngOnDestroy(): void {
      this.destroyed$.next();
   }

   setValidationRules(passwordStrengthSettingsModel: PasswordStrengthSettingsModel): void {
      this.newPasswordControl.setValidators([
         Validators.required,
         Validators.minLength(passwordStrengthSettingsModel.minimumLength),
         specialCharactersValidator(passwordStrengthSettingsModel.minimumSpecialCharacters),
         upperCaseCharactersValidator(passwordStrengthSettingsModel.minimumUpperCaseCharacters),
         numericCharactersValidator(passwordStrengthSettingsModel.minimumNumericCharacters),
         this.consecutiveCharacters(passwordStrengthSettingsModel.maxConsecutiveCharacters),
         this.passwordMatch(true),
      ]);
      this.confirmNewPasswordControl.setValidators([Validators.required, this.passwordMatch(false)]);
   }

   private createPasswordForm(): {
      [key: string]: UntypedFormControl;
   } {
      this.confirmNewPasswordControl = this.formBuilder.control(null);
      this.newPasswordControl = this.formBuilder.control(null);

      return {
         confirmNewPassword: this.confirmNewPasswordControl,
         newPassword: this.newPasswordControl,
      };
   }

   private passwordMatch(fromPasswordControl: boolean): ValidatorFn {
      return (): { [Key: string]: boolean } | null => {
         const password = this.newPasswordControl?.value;
         const confirmPassword = this.confirmNewPasswordControl?.value;
         if (fromPasswordControl) {
            this._passwordEnteredOnFormControl = password;
         }
         if (this._passwordEnteredOnFormControl !== confirmPassword) {
            if (fromPasswordControl) {
               this.confirmNewPasswordControl?.setErrors({ match: true });
               return null;
            }
            return { match: true };
         }
         if (fromPasswordControl) {
            this.confirmNewPasswordControl?.setErrors(null);
         }
         return null;
      };
   }

   private consecutiveCharacters(maxConsecutive: number): ValidatorFn {
      return (c: AbstractControl): { [key: string]: boolean } | null => {
         if (c.value != null) {
            const value: string = c.value;
            let consecutiveCount = 1;
            for (let i = 1; i < value.length; i++) {
               if (value[i] === value[i - 1]) {
                  consecutiveCount++;
                  if (consecutiveCount === maxConsecutive) {
                     return { consecutive: true };
                  }
               } else {
                  consecutiveCount = 0;
               }
            }
         }
         return null;
      };
   }
}
